import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Video, Wand2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

// New angles (team test, Oct 11 2026): one locked-off take recut into close-ups, wides
// and off-axis angles, after sferro21's "Any room, any angle" guide. Admin-only and
// behind the scene_angles setting while we check faces; nobody is charged yet, and the
// panel shows what each render costs us. Plan (free) → edit the shot list → 480p draft
// → 720p final.

type Shot = { t: number; shot: string; angle: string; move: string; still: boolean };
type AngleJob = {
  id: string; status: 'planning' | 'planned' | 'rendering' | 'ready' | 'failed'; error: string | null;
  length: number | null; shots: Shot[]; pieces: number; words: number; faceUrl: string | null;
  render: null | { n: number; resolution: '480p' | '720p'; status: string; error: string | null; costUsd: number | null; url: string | null };
  hasDraft: boolean; costUsd: number; rates: Record<'480p' | '720p', number>;
};

const SHOT_TYPES = ['WIDE', 'MEDIUM WIDE', 'MEDIUM', 'MEDIUM CLOSE-UP', 'CLOSE-UP', 'EXTREME CLOSE-UP'];
const money = (n: number) => `$${n.toFixed(2)}`;

const field: React.CSSProperties = {
  width: '100%', minHeight: 40, padding: '0 10px', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)',
  background: 'var(--hj-surface-1)', color: 'var(--hj-ink)', fontSize: 16, fontFamily: 'inherit', boxSizing: 'border-box',
};
const btn = (primary: boolean, disabled = false): React.CSSProperties => ({
  minHeight: 44, padding: '10px 16px', borderRadius: 999, border: primary ? 0 : '1px solid var(--hj-hairline)',
  background: primary ? 'var(--hj-yellow)' : 'transparent', color: primary ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)',
  fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
  display: 'inline-flex', alignItems: 'center', gap: 6,
});

export const SceneAnglesPanel: React.FC<{ videoKey: string; videoUrl: string; startTime?: number; wearing?: string; label?: string }> = ({ videoKey, videoUrl, startTime = 0, wearing = '', label }) => {
  const { getAuthHeaders } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<AngleJob | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const shotsFor = useRef('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let alive = true;
    fetch('/api/scene-angles/access', { headers: getAuthHeaders() })
      .then(r => (r.ok ? r.json() : { enabled: false })).then(d => { if (alive) setEnabled(!!d.enabled); }).catch(() => {});
    return () => { alive = false; };
  }, [getAuthHeaders]);

  // A new clip starts over
  useEffect(() => { setJobId(null); setJob(null); setShots([]); shotsFor.current = ''; setErr(''); }, [videoKey]);

  const load = useCallback(async (id: string) => {
    try {
      const r = await fetch(`/api/scene-angles/${id}`, { headers: getAuthHeaders() });
      if (r.ok) {
        const j: AngleJob = await r.json();
        setJob(j);
        if (j.shots?.length && shotsFor.current !== id) { setShots(j.shots); shotsFor.current = id; }
        if (j.status === 'planning' || j.status === 'rendering') timer.current = setTimeout(() => load(id), 6000);
        return;
      }
    } catch { /* retry */ }
    timer.current = setTimeout(() => load(id), 8000);
  }, [getAuthHeaders]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  if (!enabled || !videoKey) return null;

  const plan = async () => {
    setBusy(true); setErr('');
    try {
      const r = await fetch('/api/scene-angles/plan', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ videoKey, videoUrl, startTime, length: 60 }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Could not plan the shots.');
      setJobId(d.jobId); shotsFor.current = ''; setShots([]);
      load(d.jobId);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  const render = async (resolution: '480p' | '720p') => {
    if (!jobId) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch(`/api/scene-angles/${jobId}/render`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ shots, resolution, wearing }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.details || d.error || 'Could not start the render.');
      setJob(j => (j ? { ...j, status: 'rendering', render: { n: d.n, resolution, status: 'rendering', error: null, costUsd: null, url: null } } : j));
      if (timer.current) clearTimeout(timer.current);
      load(jobId);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  const setShot = (i: number, patch: Partial<Shot>) => setShots(prev => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const est = (res: '480p' | '720p') => (job?.length && job.rates ? money(job.length * job.rates[res]) : '');
  const rendering = job?.status === 'rendering';
  const planning = job?.status === 'planning';

  return (
    <div style={{ marginTop: 18, padding: '14px 16px', borderRadius: 'var(--hj-r-md)', border: '1px dashed var(--hj-hairline)', background: 'var(--hj-surface-1)' }}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between gap-2"
        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--hj-ink)', textAlign: 'left' }}>
        <span className="inline-flex items-center gap-2" style={{ fontSize: 14, fontWeight: 700 }}><Video className="w-4 h-4" /> New angles{label ? ` · ${label}` : ''}</span>
        <span className="mono-label">Team test · {open ? 'Hide' : 'Open'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: 13, color: 'var(--hj-ink-2)', margin: '0 0 10px', lineHeight: 1.5 }}>
            Recuts one take into close-ups, wides and side angles, cutting at your pauses. Audio is your original. Draft at 480p, then 720p.
            Only the team sees this; check the face in every shot before we open it up.
          </p>

          {!job && (
            <button type="button" onClick={plan} disabled={busy} style={btn(true, busy)}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Plan the shots · free
            </button>
          )}
          {planning && <p className="inline-flex items-center gap-2" style={{ fontSize: 13, margin: 0 }}><Loader2 className="w-4 h-4 animate-spin" /> Reading the take and finding the pauses. About a minute.</p>}

          {job && job.status !== 'planning' && shots.length > 0 && (
            <>
              <div className="flex items-center gap-3" style={{ margin: '4px 0 10px' }}>
                {job.faceUrl && <img src={job.faceUrl} alt="Face reference" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline)' }} />}
                <div style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', lineHeight: 1.45 }}>
                  {job.length?.toFixed(1)}s, {shots.length} shots{job.pieces > 1 ? `, rendered in ${job.pieces} pieces` : ''}. The face reference is the frame on the left.
                </div>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {shots.map((x, i) => (
                  <div key={i} style={{ padding: 10, borderRadius: 'var(--hj-r-sm)', border: '1px solid var(--hj-hairline-soft)', background: 'var(--hj-surface-2)', display: 'grid', gridTemplateColumns: '84px minmax(0, 1fr)', gap: 8 }}>
                    <input type="number" step={0.1} min={0} value={x.t} disabled={i === 0} aria-label="Cut at (seconds)"
                      onChange={e => setShot(i, { t: Math.max(0, parseFloat(e.target.value) || 0) })} style={field} />
                    <select value={x.shot} onChange={e => setShot(i, { shot: e.target.value })} aria-label="Shot" style={field}>
                      {SHOT_TYPES.map(t => <option key={t} value={t}>{t.toLowerCase()}</option>)}
                    </select>
                    <input value={x.angle} onChange={e => setShot(i, { angle: e.target.value })} aria-label="Angle" placeholder="angle" style={{ ...field, gridColumn: '1 / -1' }} />
                    <input value={x.still ? 'camera still' : x.move} disabled={x.still} onChange={e => setShot(i, { move: e.target.value })} aria-label="Move" placeholder="move" style={{ ...field, gridColumn: '1 / -1' }} />
                    <label className="flex items-center gap-2" style={{ gridColumn: '1 / -1', fontSize: 13, color: 'var(--hj-ink)' }}>
                      <input type="checkbox" checked={x.still} onChange={e => setShot(i, { still: e.target.checked })} style={{ width: 16, height: 16, accentColor: 'var(--hj-blue)' }} />
                      Camera still (use when the face looks wrong in this shot)
                    </label>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2" style={{ marginTop: 12 }}>
                <button type="button" onClick={() => render('480p')} disabled={busy || rendering} style={btn(true, busy || rendering)}>
                  {rendering && job.render?.resolution === '480p' ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</> : <>{job.hasDraft ? 'Redo draft' : 'Draft'} at 480p · about {est('480p')}</>}
                </button>
                {job.hasDraft && (
                  <button type="button" onClick={() => render('720p')} disabled={busy || rendering} style={btn(false, busy || rendering)}>
                    {rendering && job.render?.resolution === '720p' ? <><Loader2 className="w-4 h-4 animate-spin" /> Rendering…</> : <>Render final at 720p · about {est('720p')}</>}
                  </button>
                )}
              </div>
              {rendering && <p style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', margin: '8px 0 0' }}>Renders take several minutes. You can leave this open.</p>}
              {job.render?.status === 'ready' && job.render.url && (
                <div style={{ marginTop: 12 }}>
                  <div className="mono-label" style={{ marginBottom: 6 }}>Render {job.render.n} · {job.render.resolution}{job.render.costUsd != null ? ` · cost us ${money(job.render.costUsd)}` : ''}</div>
                  <video src={job.render.url} controls playsInline className="w-full block" style={{ borderRadius: 'var(--hj-r-md)', border: '1px solid var(--hj-hairline)', background: 'oklch(0.08 0.005 70)' }} />
                </div>
              )}
              {job.costUsd > 0 && <div className="mono-label" style={{ marginTop: 8 }}>This clip so far: {money(job.costUsd)}</div>}
            </>
          )}
          {(job?.status === 'failed' || job?.render?.status === 'failed') && (
            <p style={{ fontSize: 12.5, color: 'hsl(var(--destructive))', margin: '10px 0 0' }}>{job?.render?.error || job?.error || 'Something went wrong.'}</p>
          )}
          {job?.status === 'failed' && !shots.length && (
            <button type="button" onClick={plan} disabled={busy} style={{ ...btn(false, busy), marginTop: 8 }}>Try planning again</button>
          )}
          {err && <p style={{ fontSize: 12.5, color: 'hsl(var(--destructive))', margin: '10px 0 0' }}>{err}</p>}
        </div>
      )}
    </div>
  );
};
